import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, ProgressBar } from '../platform/_ui';
import { usePermissions } from './auth/usePermissions.js';
import { DeniedState } from './auth/DeniedState.jsx';
import { ORG } from './auth/capabilities.js';
import * as api from '../api/org-insights.api.js';

const REC_CONFIG = {
  growth:    { icon: 'TrendingUp',  tone: 'green',  color: 'var(--nx-success)', tint: 'var(--nx-success-tint)' },
  cleanup:   { icon: 'Trash2',      tone: 'amber',  color: 'var(--nx-warning)', tint: 'var(--nx-warning-tint)' },
  security:  { icon: 'Shield',      tone: 'red',    color: 'var(--nx-danger)',  tint: 'var(--nx-danger-tint)'  },
  license:   { icon: 'Key',         tone: 'amber',  color: 'var(--nx-warning)', tint: 'var(--nx-warning-tint)' },
};

const WorkspaceBar = ({ rank, name, members, activity, max, status, onClick }) => (
  <button
    type="button"
    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--nx-divider)', cursor: 'pointer', background: 'none', border: 'none', width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit' }}
    onClick={onClick}
  >
    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-faint)', width: 18, flexShrink: 0 }}>#{rank}</span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
        {status && status.toUpperCase() !== 'ACTIVE' && <Pill tone="amber">{status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}</Pill>}
      </div>
      <ProgressBar value={activity} max={max} color="var(--nx-accent)" height={4} label={name} />
    </div>
    <div style={{ textAlign: 'right', flexShrink: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{members}</div>
      <div style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>members</div>
    </div>
  </button>
);

export default function OrgInsights({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();

  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel');
  const toast      = useToast();
  const perm       = usePermissions();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading]   = useState(true);

  const org = data.state.organizations.find(o => o.id === orgId);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const i = await api.getInsights(orgId);
      setInsights(i);
    } catch { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load insights' }); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  if (!org) return null;

  if (!perm.has(ORG.ANALYTICS_VIEW)) {
    return (
      <div className="nx-cc-page">
        <PageHead title="Insights" sub={org.name} />
        <DeniedState
          reason="insufficient_permission"
          message="Insights requires the org.analytics.view capability. Contact your org admin to request access."
          action={() => navigate(`/organization/${orgId}`)}
          actionLabel="Back to Dashboard"
        />
      </div>
    );
  }

  const licUsed    = insights?.licenseUtilization?.used     ?? 0;
  const licTotal   = insights?.licenseUtilization?.allotted ?? 0;
  const licPct     = licTotal > 0 ? Math.round((licUsed / licTotal) * 100) : 0;
  const topMax     = Math.max(...(insights?.topWorkspaces        ?? []).map(w => w.members || 0), 1);
  const leastMax   = Math.max(...(insights?.leastActiveWorkspaces ?? []).map(w => w.members || 0), 1);
  const aiQuota    = insights?.aiUsage?.quota ?? 0;
  const aiTokens   = insights?.aiUsage?.totalTokens ?? 0;
  const aiPct   = aiQuota > 0 ? aiTokens / aiQuota : -1;
  let aiColor   = 'blue';
  if (aiPct >= 0.9) aiColor = 'red';
  else if (aiPct >= 0.7) aiColor = 'amber';

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Insights"
        sub={`Activity trends and growth recommendations for ${org.name}`}
        back={returnTo ? { label: returnLabel ? `Back to ${returnLabel}` : 'Back', onClick: () => navigate(returnTo) } : undefined}
        actions={<Btn variant="ghost" icon="RefreshCw" disabled={loading} onClick={load}>Refresh</Btn>}
      />

      {loading ? (
        <div className="nx-empty" style={{ padding: '48px 0' }}><NexusIcon name="Loader" size={20} /><span>Loading insights…</span></div>
      ) : !insights ? (
        <div className="nx-empty" style={{ padding: '48px 0' }}><NexusIcon name="AlertCircle" size={20} /><span>Could not load insights.</span></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* KPI tiles */}
          <div className="nx-cc-stat-grid nx-kpi-4">
            <StatCard icon="Users"      label="Total Members"    value={insights.totalMembers ?? 0}             sub="across all workspaces" color="blue"   />
            <StatCard icon="LayoutGrid" label="Total Workspaces" value={insights.totalWorkspaces ?? 0}          sub="provisioned"           color="purple" />
            <StatCard icon="Key"        label="License Usage"    value={`${licUsed} / ${licTotal}`}             sub={`${licPct}% utilized`} color={licPct > 80 ? 'red' : 'green'} />
            <StatCard icon="Cpu"        label="AI Tokens"        value={aiTokens.toLocaleString()} sub="total consumed" color={aiColor}  />
          </div>

          {/* Workspace activity */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div className="nx-cc-chart-card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Largest Workspaces</div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Ranked by member count</div>
                </div>
                <Btn variant="ghost" size="sm" onClick={() => navigate(`/organization/${orgId}/workspaces`)}>View All</Btn>
              </div>
              {(insights.topWorkspaces ?? []).length === 0 ? (
                <div className="nx-empty" style={{ padding: '24px 0' }}><span>No workspaces yet.</span></div>
              ) : (insights.topWorkspaces ?? []).map((w, i) => (
                <WorkspaceBar
                  key={w.id}
                  rank={i + 1}
                  name={w.name}
                  members={w.members}
                  activity={w.members}
                  max={topMax}
                  status={w.status}
                  onClick={() => navigate(`/organization/${orgId}/workspaces/${w.id}`)}
                />
              ))}
            </div>

            <div className="nx-cc-chart-card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Smallest Workspaces</div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>May need review or archival</div>
                </div>
                <Btn variant="ghost" size="sm" onClick={() => navigate(`/organization/${orgId}/workspaces`)}>View All</Btn>
              </div>
              {(insights.leastActiveWorkspaces ?? []).length === 0 ? (
                <div className="nx-empty" style={{ padding: '24px 0' }}><span>All workspaces are active.</span></div>
              ) : (insights.leastActiveWorkspaces ?? []).map((w, i) => (
                <WorkspaceBar
                  key={w.id}
                  rank={i + 1}
                  name={w.name}
                  members={w.members}
                  activity={w.members}
                  max={leastMax}
                  status={w.status}
                  onClick={() => navigate(`/organization/${orgId}/workspaces/${w.id}`)}
                />
              ))}
            </div>
          </div>

          {/* Recommendations — always visible so users know the panel exists (M-17) */}
          <div className="nx-cc-chart-card" style={{ padding: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Recommendations</div>
            {(insights.recommendations?.length ?? 0) === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 0', color: 'var(--nx-text-muted)', fontSize: 13 }}>
                <NexusIcon name="CheckCircle" size={16} style={{ color: 'var(--nx-success)', flexShrink: 0 }} />
                No recommendations at this time. Your organization looks healthy.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {insights.recommendations.map((r, i) => {
                  const cfg = REC_CONFIG[r.type] || { icon: 'Lightbulb', tone: 'blue', color: 'var(--nx-accent)' };
                  return (
                    <div key={r.id || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: i < insights.recommendations.length - 1 ? '1px solid var(--nx-divider)' : 'none' }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: cfg.tint || 'var(--nx-accent-tint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <NexusIcon name={cfg.icon} size={15} style={{ color: cfg.color }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)', lineHeight: 1.5 }}>{r.message}</div>
                        {r.detail && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>{r.detail}</div>}
                      </div>
                      <Pill tone={cfg.tone}>{r.type ? r.type.charAt(0).toUpperCase() + r.type.slice(1) : 'Tip'}</Pill>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
